import {
    CircularProgress,
    FormControlLabel,
    Grid,
    Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import Gutters from "Layouts/Responsive/Gutters";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxConfirmationDialog from "components/UI/AcxConfirmationDialog";
import AcxSwitch from "components/UI/AcxSwitch";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import { dateTimeColumnType } from "components/UI/AcxDataGrid/ColumnTypes/DateColTypes";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import AcxDateRangeInput from "components/UI/Calendar/DateRange/AcxDateRangeInput";
import VerticalDrawerContentTemplate from "components/UI/Drawer/VerticalDrawerContentTemplate";
import { Observer, observer } from "mobx-react";
import ProcessFailure from "models/ProcessFailure";
import { Moment } from "moment";
import React, { useEffect } from "react";
import { ServiceError } from "services/BaseService";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";
import RequeueStore from "./RequeueStore";

const Requeue = observer(() => {
    const store = useStore(RequeueStore);
    const authStore = useStore(AuthStore);
    const messageStore = useStore(MessageStore);

    const refreshData = async () => {
        try {
            await store.updateFailures();
        } catch {
            messageStore.logError(
                "Unable to fetch pipeline failures. Please retry.",
            );
        }
    };

    const requeueFailures = async () => {
        const toBulkLane = store.requeueToBulkLane;
        try {
            const count = await store.requeueFailures();
            messageStore.logMessage(
                `${count} Conversations have been successfully re-queued in the pipeline${
                    toBulkLane ? " to the bulk lane" : ""
                }`,
                "success",
            );
        } catch (error) {
            const serverMessage =
                error instanceof ServiceError ? error.errorMessage : undefined;
            messageStore.logError(
                serverMessage
                    ? `Requeue failed: ${serverMessage}`
                    : "One or more of the requeued items failed.",
            );
        }
    };

    const openFaults = async (params: GridRenderCellParams) => {
        try {
            store.selectedFailure = params.row as ProcessFailure;
            await store.updateFaults(params.row as ProcessFailure);
            store.drawerStore.closeAndResetDrawer();
            store.drawerStore.restoreDefaults();

            store.drawerStore.setContentFactory(() => (
                <Grid
                    container
                    item
                    xs={12}
                    style={{ height: "100%", width: "900px" }}
                >
                    <VerticalDrawerContentTemplate
                        title={"Faults"}
                        subTitle={store.selectedFailure?.fileName}
                        width={"900px"}
                        content={
                            <div style={{ height: "calc(100% - 2rem)" }}>
                                <div style={{ height: "100%" }}>
                                    {store.faults &&
                                    !store.faults.find(
                                        (fault) =>
                                            fault.correlationId !==
                                            store.selectedFailure?.id,
                                    ) ? (
                                        <AcxDataGrid
                                            dataGridStore={store.faultDgStore}
                                        />
                                    ) : (
                                        <CircularProgress />
                                    )}
                                </div>
                            </div>
                        }
                        onClose={() => {}}
                    />
                </Grid>
            ));

            store.drawerStore.setOpen(true);
        } catch (error) {
            store.selectedFailure = undefined;
            messageStore.logError(
                "Unable to fetch pipeline faults. Please retry.",
            );
            store.drawerStore.setOpen(false);
        }
    };

    useEffect(() => {
        if (
            !store.failures ||
            !store.failures.find(
                (failure) =>
                    failure.organizationId ===
                    authStore.orgStore.selectedOrganization?.id,
            )
        ) {
            store.updateFailures();
        }
    }, [authStore.orgStore.selectedOrganization?.id, store]);

    const columns: IColDef[] = [
        {
            headerName: "File Name",
            field: "fileName",
            type: "string",
            flex: 1,
        },
        {
            headerName: "Failed On",
            field: "processFailedOn",
            flex: 1,
            ...dateTimeColumnType,
        },
        {
            headerName: "Failure Message",
            field: "message",
            type: "string",
            flex: 1,
            valueGetter: (message) => message,
        },
        {
            headerName: "Failure Source",
            field: "source",
            type: "string",
            flex: 1,
        },
        {
            headerName: "Failure Type",
            field: "exceptionType",
            type: "string",
            flex: 1,
        },
    ];

    const faultColumns: IColDef[] = [
        {
            headerName: "Id",
            field: "id",
            type: "string",
            flex: 1,
        },
        {
            headerName: "Faulted On",
            field: "faultedOn",
            flex: 1,
            ...dateTimeColumnType,
        },
        {
            headerName: "Contract Name",
            field: "contractName",
            type: "string",
            flex: 1,
        },
        {
            headerName: "Fault Message",
            field: "message",
            type: "string",
            flex: 1,
        },
        {
            headerName: "Error Details",
            field: "exceptionDetails",
            type: "string",
            flex: 1,
        },
        {
            headerName: "Correlation Id",
            field: "correlationId",
            type: "string",
            flex: 1,
        },
        {
            headerName: "Organization Id",
            field: "organizationId",
            type: "string",
            flex: 1,
        },
        {
            headerName: "Name",
            field: "displayName",
            type: "string",
            flex: 1,
        },
        {
            headerName: "Description",
            field: "description",
            type: "string",
            flex: 1,
        },
    ];

    const controls = [
        <AcxButton
            fullWidth
            tooltip="Send Failures Back to Pipeline"
            onClick={() => store.openRequeueDialog()}
            disabled={
                !store.requeueDgStore.selectedRows ||
                store.requeueDgStore.selectedRows.length <= 0 ||
                store.requeueDgStore.isLoading ||
                store.getTaskLoading("Requeue")
            }
            loading={store.getTaskLoading("Requeue")}
        >
            Requeue Faults
        </AcxButton>,
        <AcxButton
            tooltip="Refresh data."
            color="primary"
            onClick={refreshData}
            style={{ height: "32px" }}
            loading={store.requeueDgStore.isLoading}
            disabled={store.requeueDgStore.isLoading}
        >
            <RefreshIcon />
        </AcxButton>,
        <Observer>
            {() => (
                <AcxDateRangeInput
                    labelText={"Select Date"}
                    defaultStartDate={store.datePickerStore.beginDate}
                    defaultEndDate={store.datePickerStore.endDate}
                    onSelect={(start: Moment, end: Moment) => {
                        store.datePickerStore.setBeginDate(start);
                        store.datePickerStore.setEndDate(end);
                        refreshData();
                    }}
                />
            )}
        </Observer>,
    ];

    //Data grid store setup function
    useEffect(() => {
        if (authStore.isUserUltra()) {
            columns.push({
                headerName: "Fault List",
                field: "fault",
                type: "string",
                width: 250,
                renderCell: (params) => (
                    <AcxButton fullWidth onClick={() => openFaults(params)}>
                        Open Faults
                    </AcxButton>
                ),
            });
        }
        store.requeueDgStore.setColumns(columns);
        store.requeueDgStore.controls = controls;
        store.faultDgStore.setColumns(faultColumns);
    });

    return (
        <Gutters>
            {<AcxDataGrid dataGridStore={store.requeueDgStore} />}
            <AcxConfirmationDialog
                isOpen={store.requeueDialogOpen}
                title={`Requeue ${
                    store.requeueDgStore.selectedRows?.length ?? 0
                } Conversations`}
                confirmButtonText="Requeue"
                onConfirm={requeueFailures}
                onClose={() => store.closeRequeueDialog()}
                isLoading={store.getTaskLoading("Requeue")}
                buttonsDisabled={store.getTaskLoading("Requeue")}
                content={
                    <>
                        <FormControlLabel
                            control={
                                <AcxSwitch
                                    checked={store.requeueToBulkLane}
                                    onChange={(_, checked) =>
                                        store.setRequeueToBulkLane(checked)
                                    }
                                />
                            }
                            label="Send to bulk lane (ignore hierarchy)"
                        />
                        <Typography variant="caption" component="p">
                            When enabled, the selected conversations are
                            routed to the bulk processing lane regardless of
                            the organization's lane hierarchy.
                        </Typography>
                    </>
                }
            />
        </Gutters>
    );
});

export default Requeue;
